/**
 * @providesModule @api/ReactQueryClient
 *
 * This module aims to centralize the query client and mutation management for the Skillz SDK.
 * It provides a singleton instance of the QueryClient and a SingletonMutation class for managing mutations.
 *
 */

import {Mutation, MutationFunction, MutationOptions, QueryClient} from '@tanstack/react-query';
import {ErrorResponse} from 'NetClientTypes';

export const SKZ_QUERY_KEYS = {
  USER: 'user',
  USER_PERSONAL_INFO: 'userPersonalInfo',
  PAYMENT_DATA: 'paymentData',
};

export enum SKZ_MUTATION_KEYS {
  USER_PERSONAL_INFO = 'userPersonalInfo',
  OPT_OUT_PAID_LEAGUE = 'optOutPaidLeague',
  OPT_IN_PAID_LEAGUE = 'optInPaidLeague',
  CLAIM_LEAGUE_MILESTONE_REWARD = 'claimLeagueMilestoneReward',
}

/**
 * Shared instance of QueryClient for the Skillz SDK.
 * This client is used to manage queries and mutations throughout the application via React Query.
 * It is configured with default options to avoid retries and set cache and stale times to infinity,
 * this means that once data is fetched, it will not be refetched or removed from the cache,
 * unless explicitly invalidated or removed.
 * The default options can be overridden on a per-query or per-mutation basis.
 * By using a singleton instance, we ensure that all queries and mutations share the same cache
 * and configuration, leading to better performance and consistency.
 *
 * It also allows for easy testing and mocking of the QueryClient instance
 * in unit tests, as we can replace the singleton instance with a mock implementation.
 */
export const SkillzQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  },
});

/**
 * A class that represents a singleton mutation, ensuring that a single mutation instance
 * is created and reused for a specific mutation key and function. This is useful for managing
 * mutation state and avoiding redundant mutation instances.
 *
 * @template TData - The type of the data returned by the mutation.
 * @template TVariables - The type of the variables required by the mutation.
 * @template TError - The type of the error that might be thrown by the mutation.
 * @template TContext - The type of the context shared across the mutation lifecycle.
 */
export class SkillzMutation<
  TData = unknown,
  TVariables = unknown,
  TError = ErrorResponse,
  TContext = unknown,
> {
  private mutation: Mutation<TData, TError, TVariables, TContext>;

  constructor(mutationKey: SKZ_MUTATION_KEYS, mutationFn: MutationFunction<TData, TVariables>) {
    this.mutation = SkillzQueryClient.getMutationCache().build<TData, TError, TVariables, TContext>(
      SkillzQueryClient,
      {
        mutationKey: [mutationKey],
        mutationFn,
      }
    );
  }

  /**
   * Executes the mutation with the provided options.
   *
   * @param {MutationOptions<TData, TError, TVariables, TContext>} options - The options for the mutation.
   * @returns {Promise<TData>} - A promise that resolves to the data returned by the mutation.
   */
  async mutate(options: MutationOptions<TData, TError, TVariables, TContext>) {
    this.mutation.setOptions({...this.mutation.options, ...options});
    return this.mutation.execute();
  }
}
